import type { LienFile } from "../../pages/liens/LienTypes";
import type {
  CostProjectionDataType,
  DocumentManagementDataType,
  MSADataType,
  ServiceType,
} from "../../types/DataTypes";
import { SERVICE_TYPE } from "../../types/DataTypes";

/**
 * notes entity is an object that contains the context for creating/filtering notes:
 * - fileId?: number (optional when claimId is provided)
 * - serviceId?: number (optional - for service-specific notes)
 * - lienId?: number (optional - LEGACY for lien-specific notes, kept for displaying old notes)
 * - lienFileId?: number (optional - NEW for lien file notes)
 * - claimId?: number (optional - for claim-specific notes)
 * - entityType: string (for display purposes)
 */
export type NotesEntity = {
  fileId?: number;
  serviceId?: number;
  lienId?: number; // legacy - for displaying old notes only
  lienFileId?: number; // new - for creating new lien file notes
  claimId?: number;
  entityType: string;
};

/**
 * adapters for converting service-based entities to notes entities
 *
 * @param service - the service object (MSA, LCP, etc)
 * @returns a notes entity or null if the service is undefined
 */
export const serviceToNotesEntity = (
  service: MSADataType | CostProjectionDataType | DocumentManagementDataType,
): NotesEntity | null => {
  if (!service) {
    console.warn("serviceToNotesEntity: service is undefined");
    return null;
  }

  try {
    // handle service-based entities (MSA, CostProjection, DocumentManagement)
    if ("file" in service && service.file) {
      const serviceId =
        typeof service.service === "number"
          ? service.service
          : (service.service as ServiceType)?.id;

      return {
        fileId: service.file.id,
        serviceId,
        entityType: service.type,
      };
    }
  } catch (error) {
    console.error("Error in serviceToNotesEntity:", error);
    return null;
  }
  return null;
};

/**
 * adapter for file-based notes (used in EditFile.tsx)
 *
 * @param file - the file object
 * @returns a notes entity or null if the file is undefined
 */
export const fileToNotesEntity = (file: {
  id: number;
  claim_numbers?: Array<{ number: string }>;
}): NotesEntity | null => {
  if (!file) {
    console.warn("fileToNotesEntity: file is undefined");
    return null;
  }

  try {
    return {
      fileId: file.id,
      entityType: "FILE",
    };
  } catch (error) {
    console.error("Error in fileToNotesEntity:", error);
    return null;
  }
};

/**
 * adapter for lien-based notes using lien file data
 *
 * creates new notes with lien_file association (correct approach)
 * also supports displaying legacy notes that have lien association
 *
 * @param lienFile - the lien file object
 * @returns a notes entity or null if the file is undefined
 */
export const lienFileToNotesEntity = (
  lienFile: LienFile,
): NotesEntity | null => {
  if (!lienFile) {
    console.warn("lienFileToNotesEntity: lienFile is undefined");
    return null;
  }

  try {
    return {
      fileId: Number(lienFile.file_id),
      lienFileId: lienFile.id, // new: use lien_file for creating notes
      serviceId: lienFile.service.id,
      entityType: SERVICE_TYPE.LIEN,
    };
  } catch (error) {
    console.error("Error in lienFileToNotesEntity:", error);
    return null;
  }
};

/**
 * adapter for claim-based notes
 *
 * @param claim - the claim object
 * @returns a notes entity or null if the claim is undefined
 */
export const claimToNotesEntity = (claim: {
  id: number;
}): NotesEntity | null => {
  if (!claim) {
    console.warn("claimToNotesEntity: claim is undefined");
    return null;
  }

  try {
    return {
      claimId: claim.id,
      entityType: "CLAIM",
    };
  } catch (error) {
    console.error("Error in claimToNotesEntity:", error);
    return null;
  }
};
